import { useRouter } from '@tanstack/react-router';
import { Building2, SendHorizontal, Tags, UserRound } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import {
    type MouseEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { buildActivityAssigneeOptions } from '@apps/web/components/home/activity_assignment';
import {
    type FilterChip,
    type FilterDimension,
} from '@apps/web/components/ui/filter_bar';
import {
    useOrganizationConversationsRealtime,
    useOrganizationUnreadCountRealtime,
} from '@apps/web/hooks/realtime_hooks';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    inboxCaregiverPrimaryPhone,
    inboxConversationPhoneFilter,
} from '@apps/web/lib/conversation_deep_link';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useActivityEventAssignees } from '@apps/web/swr_data_fetching/activity_event_assignments';
import { useConversationOwnershipActions } from '@apps/web/swr_data_fetching/conversation_ownership';
import {
    useOrganization,
    useOrganizationCaregiver,
    useOrganizationClient,
    useOrganizationConversations,
    useOrganizationOffices,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

import { useConversationsInboxBulkActions } from './conversations-inbox-bulk-actions';
import {
    applyConversationUnreadCountsToPages,
    persistInboxReadStates,
    revalidateOrganizationConversationPages,
} from './conversations-inbox-bulk-read';
import {
    contactPhoneDeepLinkAction,
    type ConversationsInboxDeepLink,
    LIST_LAYOUT_TRANSITION,
    noHistoryConversationRecipient,
    shouldDesktopAutoOpenConversation,
    useConversationsInboxDraft,
} from './conversations-inbox-draft';
import { useInboxCaregiverEhrLink } from './conversations-inbox-ehr-link';
import {
    CAREGIVER_TAGS_DIMENSION_KEY,
    CAREGIVER_TAGS_DIMENSION_LABEL,
    caregiverTagChipValues,
    caregiverTagFilterOptions,
    conversationMatchesAssigneeFilter,
    conversationMatchesCaregiverTagFilter,
    conversationMatchesDeliveryFilter,
    DELIVERY_DIMENSION_KEY,
    DELIVERY_DIMENSION_LABEL,
    INBOX_DELIVERY_OPTIONS,
    inboxAssigneeFilterOptions,
    shouldContinueLoadingInboxPages,
    shouldShowInboxRecencyFooter,
    useInboxCaregiverTagVocabulary,
} from './conversations-inbox-filters';
import {
    conversationPassesInboxPills,
    inboxFilterScopeKey,
    type InboxListOrder,
    type InboxStickySnapshot,
    sortInboxConversations,
    visibleInboxRows,
} from './conversations-inbox-list-order';
import { ConversationsInboxRecencyFooter } from './conversations-inbox-recency-footer';
import { ConversationsInboxSearchNotice } from './conversations-inbox-search-notice';
import { useConversationSelection } from './conversations-inbox-selection';
import { inboxLeaveNavigation } from './conversations-inbox-stay';

export type { ConversationsInboxDeepLink } from './conversations-inbox-draft';
export type ConversationsInboxSortKey = 'newest' | 'oldest';

const RECENT_CONVERSATIONS_WINDOW_DAYS = 30;

export function useConversationsInboxController(
    deepLink: ConversationsInboxDeepLink = {},
) {
    const { formatName } = useFormatPersonName();
    const router = useRouter();
    const deepLinkConversationId = deepLink.conversationId;
    const deepLinkMessageId = deepLink.messageId;
    const deepLinkCaregiverId = deepLink.caregiverId;
    const deepLinkClientId = deepLink.clientId;
    const deepLinkContactPhone = deepLink.contactPhone;
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'conversations-inbox',
    });
    const ownershipEnabled = useFeatureFlag(
        'conversation_ownership_enabled',
        organization?.features,
    );
    const replaceConversationSearch = useCallback(
        (
            nextConversationId: string | null,
            options?: { readonly keepMessageId?: boolean },
        ) => {
            router.navigate({
                to: '/conversations',
                search: (previous) => {
                    const prior = previous as {
                        conversationId?: string;
                        messageId?: string;
                        caregiverId?: string;
                        clientId?: string;
                        contactPhone?: string;
                        organizationId?: string;
                        mode?: 'live' | 'sandbox';
                    };
                    return {
                        organizationId: prior.organizationId,
                        mode: prior.mode,
                        conversationId: nextConversationId ?? undefined,
                        messageId:
                            options?.keepMessageId &&
                            nextConversationId &&
                            nextConversationId === deepLinkConversationId
                                ? deepLinkMessageId
                                : undefined,
                        caregiverId: undefined,
                        clientId: undefined,
                        contactPhone: undefined,
                    };
                },
                replace: true,
            });
        },
        [deepLinkConversationId, deepLinkMessageId, router],
    );
    const timezone = organization?.timezone ?? 'UTC';
    const { data: offices } = useOrganizationOffices(organizationId, {
        reason: 'conversations-inbox',
    });
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);
    const [chips, setChips] = useState<Array<FilterChip>>([]);
    const [sort, setSort] = useState<ConversationsInboxSortKey>('newest');
    const [recentOnly, setRecentOnly] = useState(true);
    const [selectedId, setSelectedId] = useConversationSelection(
        deepLinkConversationId,
    );
    const [selection, setSelection] = useState<ReadonlyArray<string>>([]);
    // Escape drops a selection, the way it dismisses every other transient
    // state on the page. A layer that owns the key (an open menu, a dialog,
    // the search field's own clear) marks the event handled, so the bulk bar
    // does not disappear out from under whatever the coordinator was
    // actually dismissing.
    useEffect(() => {
        if (selection.length === 0) {
            return;
        }
        function clearSelectionOnEscape(event: KeyboardEvent) {
            if (event.key !== 'Escape' || event.defaultPrevented) {
                return;
            }
            setSelection([]);
        }
        window.addEventListener('keydown', clearSelectionOnEscape);
        return () => {
            window.removeEventListener('keydown', clearSelectionOnEscape);
        };
    }, [selection.length]);
    const [profileCaregiverId, setProfileCaregiverId] = useState<string | null>(
        null,
    );
    const [activityPanelOpen, setActivityPanelOpen] = useState(false);
    // Client-only draft: selectedId stays null; isComposingNew is the selection.
    const clearConversationSelection = useCallback(() => {
        setSelectedId(null);
        replaceConversationSearch(null);
    }, [replaceConversationSearch, setSelectedId]);
    const {
        isComposingNew,
        isDraftSelected,
        draftRecipient,
        startNewConversation,
        cancelNewConversation,
        selectDraft,
        deselectDraft,
    } = useConversationsInboxDraft({
        clearSelectedConversation: clearConversationSelection,
    });
    const [openFootnoteShiftId, setOpenFootnoteShiftId] = useState<
        string | null
    >(null);

    const officeChip = chips.find((chip) => chip.dimension === 'office');

    const assigneeChip = chips.find((chip) => chip.dimension === 'assignee');
    const deliveryChip = chips.find(
        (chip) => chip.dimension === DELIVERY_DIMENSION_KEY,
    );
    const selectedIncludeCaregiverTags = caregiverTagChipValues(chips, 'is');
    const selectedExcludeCaregiverTags = caregiverTagChipValues(
        chips,
        'is_not',
    );
    const {
        conversations,
        contentMatchTruncated,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        mutate: mutateConversations,
    } = useOrganizationConversationsRealtime({
        organizationId,
        search: debouncedSearch || undefined,
        pageSize: 40,
        readState: unreadOnly ? 'unread' : undefined,
        officeIds: officeChip?.values,
        hasPendingMessages: needsReviewOnly || undefined,
        // Search spans full history; the window only bounds browsing.
        activeWithinDays:
            recentOnly && !debouncedSearch
                ? RECENT_CONVERSATIONS_WINDOW_DAYS
                : undefined,
    });
    const [openRowPin, setOpenRowPin] = useState<{
        readonly id: string;
        readonly index: number;
        readonly snapshot: OrganizationConversationMetadata | null;
        readonly filterScopeKey: string;
    } | null>(null);
    const hasClientSideFilters =
        (assigneeChip?.values.length ?? 0) > 0 ||
        (deliveryChip?.values.length ?? 0) > 0 ||
        selectedIncludeCaregiverTags.length > 0 ||
        selectedExcludeCaregiverTags.length > 0;
    useEffect(() => {
        if (
            !shouldContinueLoadingInboxPages({
                hasClientSideFilters,
                isLoadingMore,
                isReachingEnd,
            })
        ) {
            return;
        }
        void setSize(size + 1);
    }, [hasClientSideFilters, isLoadingMore, isReachingEnd, setSize, size]);
    const caregiverTagVocabulary =
        useInboxCaregiverTagVocabulary(organizationId);
    const { data: assigneeVocabulary } = useActivityEventAssignees(
        organizationId,
        organizationId ? [organizationId] : [],
    );
    const assigneeOptions = useMemo(
        () =>
            inboxAssigneeFilterOptions(
                conversations,
                buildActivityAssigneeOptions(
                    assigneeVocabulary?.users ?? [],
                ).map((option) => ({
                    value: option.id,
                    label: option.label,
                })),
                true,
            ),
        [assigneeVocabulary?.users, conversations],
    );
    const { assign } = useConversationOwnershipActions(
        organizationId,
        mutateConversations,
    );
    const mutateConversationsRef = useRef(mutateConversations);
    mutateConversationsRef.current = mutateConversations;
    const skipPillRevalidateOnMountRef = useRef(true);
    useEffect(() => {
        if (skipPillRevalidateOnMountRef.current) {
            skipPillRevalidateOnMountRef.current = false;
            return;
        }
        // Bound mutate follows the current SWR key. Revalidate the newly
        // selected All / Unread / Needs review cache on pill change; a
        // warm sibling cache can otherwise stay stale for the 60s floor.
        void mutateConversationsRef.current();
    }, [unreadOnly, needsReviewOnly]);
    const { data: unreadCount, mutate: mutateUnreadCount } =
        useOrganizationUnreadCountRealtime(
            'conversations-inbox',
            true,
            organizationId,
        );
    const persistConversationReadState = useCallback(
        async (conversationId: string, nextUnreadCount: 0 | 1) => {
            if (!organizationId) {
                return;
            }
            const { succeededIds, failedIds } = await persistInboxReadStates(
                organizationId,
                [conversationId],
                nextUnreadCount,
            );
            if (succeededIds.length > 0) {
                if (succeededIds.includes(conversationId)) {
                    setOpenRowPin((current) =>
                        current?.id === conversationId && current.snapshot
                            ? {
                                  ...current,
                                  snapshot: {
                                      ...current.snapshot,
                                      unread_count: nextUnreadCount,
                                  },
                              }
                            : current,
                    );
                }
                await mutateConversations(
                    (pages) =>
                        applyConversationUnreadCountsToPages(
                            pages,
                            new Set(succeededIds),
                            nextUnreadCount,
                        ),
                    { revalidate: true },
                );
                void revalidateOrganizationConversationPages(organizationId);
                void mutateUnreadCount();
            }
            if (failedIds.length > 0) {
                throw new Error('Could not update read state');
            }
        },
        [mutateConversations, mutateUnreadCount, organizationId],
    );
    // markAsRead skips list revalidation and relies on realtime; still
    // persist then refetch so optimistic unread updates land immediately.
    const persistMarkAsRead = useCallback(
        async (conversationId: string) => {
            await persistConversationReadState(conversationId, 0);
        },
        [persistConversationReadState],
    );
    const persistMarkAsUnread = useCallback(
        async (conversationId: string) => {
            await persistConversationReadState(conversationId, 1);
        },
        [persistConversationReadState],
    );
    const prefersReducedMotion = useReducedMotion();
    const listLayoutTransition = prefersReducedMotion
        ? { duration: 0 }
        : LIST_LAYOUT_TRANSITION;

    const filterDimensions = useMemo<Array<FilterDimension>>(
        () => [
            {
                key: 'office',
                label: 'Office',
                icon: Building2,
                multiSelect: true,
                searchable: true,
                options: (offices ?? []).map((office) => ({
                    value: office.id,
                    label: office.display_name,
                })),
            },
            {
                key: 'assignee',
                label: 'Assignee',
                icon: UserRound,
                multiSelect: true,
                searchable: true,
                options: assigneeOptions,
            },
            {
                key: CAREGIVER_TAGS_DIMENSION_KEY,
                label: CAREGIVER_TAGS_DIMENSION_LABEL,
                icon: Tags,
                operators: ['is', 'is_not'],
                multiSelect: true,
                searchable: true,
                options: caregiverTagFilterOptions([
                    ...caregiverTagVocabulary,
                    ...selectedIncludeCaregiverTags,
                    ...selectedExcludeCaregiverTags,
                ]),
            },
            {
                key: DELIVERY_DIMENSION_KEY,
                label: DELIVERY_DIMENSION_LABEL,
                icon: SendHorizontal,
                multiSelect: true,
                options: [...INBOX_DELIVERY_OPTIONS],
            },
        ],
        [
            caregiverTagVocabulary,
            assigneeOptions,
            offices,
            selectedExcludeCaregiverTags,
            selectedIncludeCaregiverTags,
        ],
    );

    const matchingRows = useMemo(
        () =>
            conversations.filter((conversation) => {
                if (
                    !conversationMatchesAssigneeFilter(
                        conversation,
                        assigneeChip?.values ?? [],
                    )
                ) {
                    return false;
                }
                if (
                    !conversationMatchesCaregiverTagFilter(
                        conversation,
                        selectedIncludeCaregiverTags,
                        selectedExcludeCaregiverTags,
                    )
                ) {
                    return false;
                }
                if (
                    !conversationMatchesDeliveryFilter(
                        conversation,
                        deliveryChip?.values ?? [],
                    )
                ) {
                    return false;
                }
                return true;
            }),
        [
            assigneeChip?.values,
            conversations,
            deliveryChip?.values,
            selectedExcludeCaregiverTags,
            selectedIncludeCaregiverTags,
        ],
    );
    const listOrder: InboxListOrder =
        debouncedSearch.trim().length > 0 ? 'api-relevance' : sort;
    const sortedRows = useMemo(
        () =>
            sortInboxConversations(
                matchingRows.filter((conversation) =>
                    conversationPassesInboxPills(
                        conversation,
                        unreadOnly,
                        needsReviewOnly,
                    ),
                ),
                {
                    order: listOrder,
                },
            ),
        [listOrder, matchingRows, needsReviewOnly, unreadOnly],
    );
    // A saved row may bridge the same-scope mark-read refetch, but it must not
    // leak through a search, office, ownership, tag, blocked, recency, or org
    // change that intentionally removes it from the result set.
    const filterScopeKey = inboxFilterScopeKey({
        organizationId,
        search: debouncedSearch,
        officeIds: officeChip?.values ?? [],
        assigneeIds: assigneeChip?.values ?? [],
        includedCaregiverTags: selectedIncludeCaregiverTags,
        excludedCaregiverTags: selectedExcludeCaregiverTags,
        deliveryStates: deliveryChip?.values ?? [],
        recentOnly,
        unreadOnly,
        needsReviewOnly,
    });
    const viewOrderKey = `${listOrder}:${unreadOnly}:${needsReviewOnly}:${filterScopeKey}`;
    const sortedRowsRef = useRef(sortedRows);
    sortedRowsRef.current = sortedRows;
    // Rows read during this visit to a pill view. Opening a thread clears its
    // unread dot immediately, but the row has to stay put until the
    // coordinator stops triaging, or the list unmounts a row out from under
    // the click that is still in progress.
    const [stickyRows, setStickyRows] = useState<
        ReadonlyMap<string, InboxStickySnapshot>
    >(new Map());
    const pillActive = unreadOnly || needsReviewOnly;
    const pillActiveRef = useRef(pillActive);
    pillActiveRef.current = pillActive;
    // Nothing clears the set while the coordinator is still on Conversations:
    // toggling a pill, sorting, and searching all keep the rows in place. The
    // set lives and dies with this hook, so leaving the page is the only
    // thing that releases it -- come back and the list is honest again.
    const openConversation = useCallback(
        (conversationId: string) => {
            const index = sortedRowsRef.current.findIndex(
                (conversation) => conversation.id === conversationId,
            );
            const opened =
                index >= 0 ? sortedRowsRef.current[index] : undefined;
            // Snapshot before the read/pending state changes underneath us.
            if (opened !== undefined && pillActiveRef.current) {
                setStickyRows((current) => {
                    if (current.has(conversationId)) {
                        return current;
                    }
                    const next = new Map(current);
                    next.set(conversationId, {
                        unread_count: opened.unread_count,
                        pending_staged_message_count:
                            opened.pending_staged_message_count,
                    });
                    return next;
                });
            }
            // Reading another thread hands the pane over but leaves the
            // draft in the list. An unfinished message must not be one click
            // from gone.
            deselectDraft();
            setSelectedId(conversationId);
            setOpenRowPin({
                id: conversationId,
                index: index >= 0 ? index : 0,
                snapshot: opened ?? null,
                filterScopeKey,
            });
            replaceConversationSearch(conversationId);
        },
        [
            deselectDraft,
            filterScopeKey,
            replaceConversationSearch,
            setSelectedId,
        ],
    );
    // A thread the coordinator just started belongs at the top of the list,
    // the way a new message behaves in a phone's messages app. The open-row
    // pin already holds position 0, but the Unread and Needs review pills
    // filter server-side, so a brand-new conversation is never fetched while
    // one of them is on -- there would be no row to pin. Clearing them is
    // what makes the new thread reachable at all.
    const openCreatedConversation = useCallback(
        (conversationId: string) => {
            setUnreadOnly(false);
            setNeedsReviewOnly(false);
            // The draft became this thread, so its row has to go or the list
            // shows a "New conversation" that no longer exists.
            cancelNewConversation();
            openConversation(conversationId);
        },
        [cancelNewConversation, openConversation],
    );

    useLayoutEffect(() => {
        if (selectedId === null) {
            setOpenRowPin(null);
            return;
        }
        const index = sortedRowsRef.current.findIndex(
            (conversation) => conversation.id === selectedId,
        );
        if (index >= 0) {
            setOpenRowPin({
                id: selectedId,
                index,
                snapshot: sortedRowsRef.current[index] ?? null,
                filterScopeKey,
            });
            return;
        }
        // Mark-as-read or a cleared pending reply can drop the open row
        // from Unread / Needs review. Keep the pin so it can re-insert.
        setOpenRowPin((current) =>
            current?.id === selectedId &&
            current.filterScopeKey === filterScopeKey
                ? current
                : {
                      id: selectedId,
                      index: 0,
                      snapshot: null,
                      filterScopeKey,
                  },
        );
    }, [filterScopeKey, selectedId, viewOrderKey]);
    const rows = useMemo(
        () =>
            visibleInboxRows(
                matchingRows,
                {
                    order: listOrder,
                    unreadOnly,
                    needsReviewOnly,
                    openId: openRowPin?.id ?? selectedId,
                    pinnedIndex: openRowPin?.index ?? null,
                    filterScopeKey,
                    stickyRows,
                },
                openRowPin?.snapshot
                    ? {
                          filterScopeKey: openRowPin.filterScopeKey,
                          row: openRowPin.snapshot,
                      }
                    : null,
            ),
        [
            matchingRows,
            needsReviewOnly,
            filterScopeKey,
            openRowPin,
            selectedId,
            listOrder,
            stickyRows,
            unreadOnly,
        ],
    );
    const isLoadingFilteredPages = hasClientSideFilters && !isReachingEnd;
    // A draft is a row on screen even though it is not a row of data, so an
    // inbox with nothing but a draft in it is not empty -- rendering the
    // "no matches" state there would hide the draft the coordinator just
    // started.
    const isEmpty =
        !isLoading &&
        !isLoadingFilteredPages &&
        rows.length === 0 &&
        !isComposingNew;
    useEffect(() => {
        const visibleIds = new Set(rows.map((row) => row.id));
        setSelection((current) => {
            const next = current.filter((id) => visibleIds.has(id));
            return next.length === current.length ? current : next;
        });
    }, [rows]);
    const hasSelection = selection.length > 0;
    const isMobile = useIsMobile();
    const shouldResolveCaregiver =
        !!deepLinkCaregiverId && !deepLinkConversationId;
    const shouldResolveClient =
        !!deepLinkClientId && !deepLinkConversationId && !deepLinkCaregiverId;
    const resolvedContactPhone =
        inboxConversationPhoneFilter(deepLinkContactPhone);
    const shouldResolveContactPhone =
        !!resolvedContactPhone &&
        !deepLinkConversationId &&
        !deepLinkCaregiverId &&
        !deepLinkClientId;
    const { data: deepLinkCaregiver, isLoading: isLoadingDeepLinkCaregiver } =
        useOrganizationCaregiver(
            shouldResolveCaregiver ? organizationId : null,
            shouldResolveCaregiver ? (deepLinkCaregiverId ?? null) : null,
            { reason: 'conversations-inbox-caregiver-deep-link' },
        );
    const { data: deepLinkClient, isLoading: isLoadingDeepLinkClient } =
        useOrganizationClient(
            shouldResolveClient ? organizationId : null,
            shouldResolveClient ? (deepLinkClientId ?? null) : null,
            { reason: 'conversations-inbox-client-deep-link' },
        );
    const clientContactPhone = inboxConversationPhoneFilter(
        deepLinkClient?.phone_number,
    );
    const phoneLookupNumber = shouldResolveClient
        ? clientContactPhone
        : resolvedContactPhone;
    const shouldLookupPhone =
        shouldResolveContactPhone ||
        (shouldResolveClient && !!phoneLookupNumber);
    // Only the newest match is read below, so fetch one row instead of a
    // full 30-row page.
    const {
        conversations: caregiverConversations,
        isLoading: isLoadingCaregiverConversations,
    } = useOrganizationConversations(
        shouldResolveCaregiver ? organizationId : null,
        { reason: 'conversations-inbox-caregiver-deep-link' },
        undefined,
        {
            caregiverId: deepLinkCaregiverId,
            enabled: shouldResolveCaregiver,
            pageSize: 1,
        },
    );
    const {
        conversations: contactPhoneConversations,
        isLoading: isLoadingContactPhoneConversations,
    } = useOrganizationConversations(
        shouldLookupPhone ? organizationId : null,
        { reason: 'conversations-inbox-contact-phone-deep-link' },
        undefined,
        {
            contactPhoneNumber: phoneLookupNumber,
            enabled: shouldLookupPhone,
            pageSize: 1,
        },
    );
    // Resolve person deep links to that person's newest conversation.
    // No history still opens a draft. Caregiver and phone/client entry
    // are mutually exclusive, so one state machine owns wait/open/compose.
    const deepLinkResolution = useMemo(() => {
        if (shouldResolveCaregiver) {
            return {
                shouldResolve: true,
                isLoading:
                    isLoadingCaregiverConversations ||
                    isLoadingDeepLinkCaregiver,
                conversationId: caregiverConversations[0]?.id,
                recipient: noHistoryConversationRecipient({
                    caregiverId: deepLinkCaregiverId,
                    caregiverName: formatName(deepLinkCaregiver?.name),
                    phone:
                        inboxCaregiverPrimaryPhone(deepLinkCaregiver) ??
                        resolvedContactPhone,
                }),
            };
        }
        return {
            shouldResolve: shouldResolveContactPhone || shouldResolveClient,
            isLoading:
                (shouldResolveClient && isLoadingDeepLinkClient) ||
                (shouldLookupPhone && isLoadingContactPhoneConversations),
            conversationId: contactPhoneConversations[0]?.id,
            recipient: phoneLookupNumber
                ? {
                      kind: 'phoneNumber' as const,
                      phoneNumber: phoneLookupNumber,
                  }
                : null,
        };
    }, [
        caregiverConversations,
        contactPhoneConversations,
        deepLinkCaregiver,
        deepLinkCaregiverId,
        isLoadingCaregiverConversations,
        isLoadingContactPhoneConversations,
        isLoadingDeepLinkCaregiver,
        isLoadingDeepLinkClient,
        phoneLookupNumber,
        resolvedContactPhone,
        formatName,
        shouldLookupPhone,
        shouldResolveCaregiver,
        shouldResolveClient,
        shouldResolveContactPhone,
    ]);
    useEffect(() => {
        const action = contactPhoneDeepLinkAction({
            shouldResolve: deepLinkResolution.shouldResolve,
            isLoading: deepLinkResolution.isLoading,
            conversationId: deepLinkResolution.conversationId,
        });
        if (action === 'wait') {
            return;
        }
        if (action === 'open') {
            const conversationId = deepLinkResolution.conversationId;
            if (!conversationId) {
                return;
            }
            setSelectedId(conversationId);
            replaceConversationSearch(conversationId);
            return;
        }
        if (isComposingNew || !deepLinkResolution.recipient) {
            return;
        }
        startNewConversation(deepLinkResolution.recipient);
    }, [
        deepLinkResolution.conversationId,
        deepLinkResolution.isLoading,
        deepLinkResolution.recipient,
        deepLinkResolution.shouldResolve,
        isComposingNew,
        replaceConversationSearch,
        setSelectedId,
        startNewConversation,
    ]);
    // Auto-open the newest-activity thread, not the first row of the current
    // sort: pending-first can put a stale approval at the top (PHO-16873).
    // Skip on mobile so the list stays the entry surface until a row tap.
    // Skip while composing a draft or while a deep link is driving selection.
    useEffect(() => {
        if (
            !shouldDesktopAutoOpenConversation({
                isMobile,
                selectedId,
                isComposingNew,
                deepLinkConversationId,
                shouldResolveCaregiver,
                shouldResolveClient,
                // An unnormalizable contactPhone still counts as an active
                // person deep link, so desktop auto-open must not steal the
                // newest unrelated thread.
                shouldResolveContactPhone:
                    shouldResolveContactPhone || !!deepLinkContactPhone,
                isLoading,
                hasRows: sortedRows.length > 0,
            })
        ) {
            return;
        }
        let newest: (typeof sortedRows)[number] | undefined;
        for (const conversation of sortedRows) {
            if (
                !newest ||
                new Date(conversation.last_active_at).getTime() >
                    new Date(newest.last_active_at).getTime()
            ) {
                newest = conversation;
            }
        }
        if (newest) {
            setSelectedId(newest.id);
            replaceConversationSearch(newest.id);
        }
    }, [
        deepLinkConversationId,
        isComposingNew,
        isLoading,
        isMobile,
        replaceConversationSearch,
        selectedId,
        setSelectedId,
        shouldResolveCaregiver,
        shouldResolveClient,
        shouldResolveContactPhone,
        deepLinkContactPhone,
        sortedRows,
    ]);
    // Never fall back to rows[0] while selectedId is null — that mounted the
    // detail pane during desktop init and marked the first row read early.
    const selectedConversation =
        selectedId === null
            ? undefined
            : (rows.find((conversation) => conversation.id === selectedId) ??
              conversations.find(
                  (conversation) => conversation.id === selectedId,
              ));
    const recentWindowDays =
        recentOnly && !debouncedSearch
            ? RECENT_CONVERSATIONS_WINDOW_DAYS
            : null;
    const searchNotice = (
        <ConversationsInboxSearchNotice
            contentMatchTruncated={!!debouncedSearch && contentMatchTruncated}
        />
    );
    const recencyFooter = shouldShowInboxRecencyFooter(debouncedSearch) ? (
        <ConversationsInboxRecencyFooter
            isReachingEnd={isReachingEnd}
            onShowOlder={() => setRecentOnly(false)}
            onShowRecentOnly={() => setRecentOnly(true)}
            recentWindowDays={recentWindowDays}
        />
    ) : null;
    const caregiverEhrLink = useInboxCaregiverEhrLink(
        organizationId,
        selectedConversation?.caregiver_id ?? null,
    );
    const selectedConversations = useMemo(
        () =>
            rows.filter((conversation) => selection.includes(conversation.id)),
        [rows, selection],
    );
    const {
        bulkAssignee,
        handleMarkAllAsRead,
        isBulkReadPending,
        isMarkingAllAsRead,
        onBulkAssign,
        onBulkMarkReadState,
    } = useConversationsInboxBulkActions({
        assign,
        mutateConversations,
        mutateUnreadCount,
        organizationId,
        selectedConversations,
        selection,
        unreadCount,
    });

    const loadMoreConversations = useCallback(() => {
        if (isLoadingMore || isReachingEnd) {
            return;
        }
        void setSize(size + 1);
    }, [isLoadingMore, isReachingEnd, setSize, size]);

    const onInboxLinkCapture = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            // Dialogs and menus portal to body but still bubble through this
            // React tree. Don't swallow recipient clicks or EHR link-outs.
            if (
                target.closest('[data-slot="dialog-content"]') ||
                target.closest('[data-slot="dropdown-menu-content"]') ||
                target.closest('[role="dialog"]')
            ) {
                return;
            }
            const anchor = target.closest('a');
            if (!(anchor instanceof HTMLAnchorElement)) {
                return;
            }
            if (anchor.target === '_blank') {
                return;
            }
            const navigation = inboxLeaveNavigation(anchor.href);
            if (!navigation.leaves) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            if (navigation.caregiverId) {
                setProfileCaregiverId(navigation.caregiverId);
            }
        },
        [],
    );

    return {
        activityPanelOpen,
        assign,
        bulkAssignee,
        caregiverEhrLink,
        chips,
        filterDimensions,
        handleMarkAllAsRead,
        hasSelection,
        isBulkReadPending,
        isEmpty,
        isLoadingMore,
        isMarkingAllAsRead,
        isMobile,
        isComposingNew,
        startNewConversation,
        cancelNewConversation,
        isDraftSelected,
        draftRecipient,
        selectDraft,
        isReachingEnd,
        listLayoutTransition,
        loadMoreConversations,
        mutateConversations,
        needsReviewOnly,
        onBulkAssign,
        onBulkMarkReadState,
        onInboxLinkCapture,
        clearSelectedConversation: clearConversationSelection,
        highlightMessageId: deepLinkMessageId,
        openConversation,
        openCreatedConversation,
        openFootnoteShiftId,
        organizationId,
        ownershipEnabled,
        persistMarkAsRead,
        persistMarkAsUnread,
        profileCaregiverId,
        recentWindowDays,
        recencyFooter,
        rows,
        search,
        searchNotice,
        debouncedSearch,
        selectedConversation,
        selectedConversations,
        selectedId,
        selection,
        setActivityPanelOpen,
        setChips,
        setNeedsReviewOnly,
        setOpenFootnoteShiftId,
        setProfileCaregiverId,
        setRecentOnly,
        setSearch,
        setSelectedId,
        setSelection,
        setSort,
        setUnreadOnly,
        sort,
        timezone,
        unreadCount,
        unreadOnly,
    };
}
